import React, { useState, useEffect } from 'react';
import { applicationApi } from '../services/api';
import './AnalysisReportForm.css';

interface PassportInfo {
  number: string;
  dob: string;
  expiry: string;
}

interface AddressInfo {
  street: string;
  city: string;
  province: string;
  postal_code: string;
  source_docs: string[];
}

interface T4Info {
  employment_income: number;
  income_tax_deducted: number;
  cpp: number;
  ei: number;
  employer: string;
  employer_bn: string;
}

interface TaxReturnInfo {
  employment_income: number;
  total_income: number;
  net_income: number;
  taxable_income: number;
  federal_tax: number;
  provincial_tax: number;
  refund_or_balance: number;
}

interface PaystubInfo {
  period_start: string;
  period_end: string;
  pay_date: string;
  gross_current: number;
  deductions_current: number;
  net_current: number;
  ytd_gross: number;
  ytd_net: number;
  sin: string;
  employer: string;
}

interface BankAccount {
  type: string;
  masked_number: string;
  available_balance: number;
  as_of_date: string;
  source: string;
}

interface AssetsInfo {
  bank_accounts: BankAccount[];
  total_verified_funds: number;
  pof_valid_until: string;
  pof_reference: string;
}

interface ConsistencyCheck {
  check: string;
  result: string;
  notes: string | null;
}

interface ScoringInfo {
  person_id: string;
  final_score: number;
  fraud_risk_score: number;
  decision: string;
  category_scores: {
    identity: number;
    income: number;
    financial: number;
    timeline: number;
    supporting: number;
  };
  consistency_penalties: number;
  fraud_breakdown: {
    [key: string]: number;
  };
  red_flags: string[];
  calc_trace: any;
}

interface AnalysisData {
  application_id: string;
  generated_at: string;
  source_container: string;
  source_prefix: string;
  facts: {
    applicant_name: string;
    ids: {
      passport: PassportInfo;
    };
    address: AddressInfo;
    income: {
      t4_2024: T4Info;
      tax_return_2024: TaxReturnInfo;
      latest_paystub: PaystubInfo;
    };
    assets: AssetsInfo;
    consistency_checks: ConsistencyCheck[];
    risk_flags: string[];
    missing_fields: string[];
    notes: string[];
    confidence: number;
  };
  scoring?: ScoringInfo;
}

interface AnalysisReportFormProps {
  data: AnalysisData;
  onClose?: () => void;
}

const AnalysisReportForm: React.FC<AnalysisReportFormProps> = ({ data, onClose }) => {
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [underwriterNote, setUnderwriterNote] = useState<string>('');
  const [noteLoading, setNoteLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState<any>(null);

  useEffect(() => {
    // Load current user
    const userStr = localStorage.getItem('user');
    if (userStr) {
      setCurrentUser(JSON.parse(userStr));
    }

    // Load existing underwriter note
    loadUnderwriterNote();
  }, [data.application_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadUnderwriterNote = async () => {
    try {
      setNoteLoading(true);
      const app = await applicationApi.getApplication(data.application_id);
      setUnderwriterNote(app.underwriter_note || '');
    } catch (err) {
      console.error('Error loading underwriter note:', err);
    } finally {
      setNoteLoading(false);
    }
  };

  const saveUnderwriterNote = async () => {
    try {
      await applicationApi.updateUnderwriterNote(data.application_id, underwriterNote);
    } catch (err) {
      console.error('Error saving underwriter note:', err);
      throw err;
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateStr: string): string => {
    // For date-only strings (YYYY-MM-DD), parse as local date to avoid timezone shift
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [year, month, day] = dateStr.split('-');
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day)).toLocaleDateString('en-CA');
    }
    return new Date(dateStr).toLocaleDateString('en-CA');
  };

  const getCheckStatusIcon = (result: string): string => {
    return result === 'pass' ? '✅' : result === 'fail' ? '❌' : '⚠️';
  };

  const handleStatusUpdate = async (status: 'Approved' | 'Rejected') => {
    try {
      setUpdating(true);
      setError(null);
      
      // Save underwriter note first
      await saveUnderwriterNote();
      
      // Then update status
      await applicationApi.updateApplication(data.application_id, { status });
      
      if (onClose) {
        onClose();
      }
    } catch (err) {
      setError(`Failed to update application status`);
      console.error('Error updating status:', err);
    } finally {
      setUpdating(false);
    }
  };

  const handleClose = async () => {
    try {
      setUpdating(true);
      // Save underwriter note before closing
      await saveUnderwriterNote();
      if (onClose) {
        onClose();
      }
    } catch (err) {
      console.error('Error saving note on close:', err);
      // Close anyway even if save fails
      if (onClose) {
        onClose();
      }
    } finally {
      setUpdating(false);
    }
  };

  // Safety check for required data
  if (!data || !data.facts) {
    return (
      <div className="analysis-report-overlay">
        <div className="analysis-report-form">
          <div className="analysis-header">
            <h2>📊 Analysis Report</h2>
            <button className="close-btn" onClick={onClose}>×</button>
          </div>
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <p>Error: Invalid analysis data structure</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="analysis-report-overlay">
      <div className="analysis-report-form">
        <div className="analysis-header">
          <div className="header-content">
            <h2>📊 Analysis Report</h2>
            <div className="header-meta">
              <span className="header-meta-item">
                Generated: {new Date(data.generated_at).toLocaleString('en-CA')}
              </span>
              <span className="header-meta-item">
                Confidence: {data.facts.confidence <= 1 
                  ? (data.facts.confidence * 100).toFixed(1) 
                  : data.facts.confidence.toFixed(1)}%
              </span>
            </div>
          </div>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="analysis-content-wrapper">
          {/* Verification Results - Sticky Top Right */}
          <aside className="verification-sidebar">
            {/* Scoring Section */}
            {data.scoring && (
              <section className="scoring-info">
                <h3>📊 Application Scoring</h3>
                
                {/* Decision Badge */}
                <div className={`decision-badge decision-${data.scoring.decision.toLowerCase()}`}>
                  <span className="decision-label">Decision:</span>
                  <span className="decision-value">{data.scoring.decision}</span>
                </div>

                {/* Overall Scores */}
                <div className="card">
                  <div className="card-header">
                    <h4>Overall Scores</h4>
                  </div>
                  <div className="card-content">
                    <div className="score-item">
                      <label>Final Score:</label>
                      <span className="score-value">{data.scoring.final_score}</span>
                    </div>
                    <div className="score-item fraud-risk">
                      <label>Fraud Risk Score:</label>
                      <span className="score-value">{data.scoring.fraud_risk_score}</span>
                    </div>
                  </div>
                </div>

                {/* Category Scores */}
                <div className="card">
                  <div className="card-header">
                    <h4>Category Scores</h4>
                  </div>
                  <div className="card-content">
                    {Object.entries(data.scoring.category_scores).map(([category, score]) => (
                      <div key={category} className="category-score-item">
                        <label>{category.charAt(0).toUpperCase() + category.slice(1)}:</label>
                        <div className="score-bar-wrapper">
                          <div className="score-bar" style={{ width: `${score}%` }}>
                            <span className="score-bar-value">{score}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Fraud Breakdown */}
                {data.scoring.fraud_breakdown && Object.keys(data.scoring.fraud_breakdown).length > 0 && (
                  <div className="card fraud-card">
                    <div className="card-header">
                      <h4>🚨 Fraud Risk Breakdown</h4>
                    </div>
                    <div className="card-content">
                      {Object.entries(data.scoring.fraud_breakdown).map(([type, score]) => (
                        <div key={type} className="fraud-score-item">
                          <label>{type.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}:</label>
                          <span className={`fraud-score-value ${Number(score) > 30 ? 'high' : Number(score) > 15 ? 'medium' : 'low'}`}>
                            {score}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Consistency Penalties */}
                {data.scoring.consistency_penalties > 0 && (
                  <div className="penalty-notice">
                    ⚠️ Consistency Penalties: {data.scoring.consistency_penalties}
                  </div>
                )}

                {/* Red Flags */}
                {data.scoring.red_flags && data.scoring.red_flags.length > 0 && (
                  <div className="card">
                    <div className="card-header">
                      <h4>🚩 Red Flags</h4>
                    </div>
                    <div className="card-content">
                      <ul className="red-flags-list">
                        {data.scoring.red_flags.map((flag: string, index: number) => (
                          <li key={index} className="red-flag-item">{flag}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </section>
            )}

            <section className="verification-info">
              <h3>✅ Verification Results</h3>
              <div className="card">
                <div className="card-header">
                  <h4>Consistency Checks</h4>
                </div>
                <div className="card-content">
                  <div className="checks-list">
                    {data.facts.consistency_checks.map((check, index) => (
                      <div key={index} className={`check-item ${check.result}`}>
                        <span className="check-icon">{getCheckStatusIcon(check.result)}</span>
                        <span className="check-text">{check.check}</span>
                        {check.notes && <span className="check-notes">{check.notes}</span>}
                      </div>
                    ))}
                  </div>

                  {/* Risk Flags */}
                  {data.facts.risk_flags.length > 0 && (
                    <div className="subsection">
                      <h5 className="subsection-header">⚠️ Risk Flags</h5>
                      <ul className="flags-list">
                        {data.facts.risk_flags.map((flag, index) => (
                          <li key={index} className="risk-flag">{flag}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Missing Fields */}
                  {data.facts.missing_fields.length > 0 && (
                    <div className="subsection">
                      <h5 className="subsection-header">❓ Missing Information</h5>
                      <ul className="missing-list">
                        {data.facts.missing_fields.map((field, index) => (
                          <li key={index} className="missing-field">{field}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Notes */}
                  {data.facts.notes.length > 0 && (
                    <div className="subsection">
                      <h5 className="subsection-header">📝 Notes</h5>
                      <ul className="notes-list">
                        {data.facts.notes.map((note, index) => (
                          <li key={index} className="note-item">{note}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </section>

            {/* Underwriter Notes Section */}
            {currentUser?.is_admin && (
              <section className="underwriter-notes-section-analysis">
                <h3>📝 Underwriter Notes</h3>
                <div className="card">
                  <div className="card-content">
                    {noteLoading ? (
                      <div className="loading-note">Loading notes...</div>
                    ) : (
                      <textarea
                        value={underwriterNote}
                        onChange={(e) => setUnderwriterNote(e.target.value)}
                        placeholder="Enter internal notes for underwriting purposes..."
                        rows={8}
                        className="underwriter-textarea-analysis"
                      />
                    )}
                    <div className="note-hint">
                      Note will be saved automatically when you approve/reject or close the report.
                    </div>
                  </div>
                </div>
              </section>
            )}
          </aside>

          {/* Main Content - Scrollable */}
          <div className="analysis-content">
          {/* Applicant Information */}
          <section className="applicant-info">
            <h3>👤 Applicant Information</h3>
            <div className="card">
              <div className="card-header">
                <h4>{data.facts.applicant_name}</h4>
              </div>
              <div className="card-content">
                <div className="info-group">
                  <h5>🛂 Identification</h5>
                  <div className="info-grid">
                    <div className="info-item">
                      <label>Passport Number:</label>
                      <span className="mono">{data.facts.ids.passport.number}</span>
                    </div>
                    <div className="info-item">
                      <label>Date of Birth:</label>
                      <span>{formatDate(data.facts.ids.passport.dob)}</span>
                    </div>
                    <div className="info-item">
                      <label>Passport Expiry:</label>
                      <span>{formatDate(data.facts.ids.passport.expiry)}</span>
                    </div>
                  </div>
                </div>

                <div className="info-group">
                  <h5>🏠 Address</h5>
                  <div className="address-block">
                    <p>{data.facts.address.street}</p>
                    <p>{data.facts.address.city}, {data.facts.address.province} {data.facts.address.postal_code}</p>
                  </div>
                  <div className="source-docs">
                    <h6>📄 Verified by documents:</h6>
                    <ul>
                      {data.facts.address.source_docs.map((doc, index) => (
                        <li key={index}>{doc}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Income Information */}
          <section className="income-info">
            <h3>💰 Income Information</h3>
            
            {/* T4 Information */}
            <div className="card">
              <div className="card-header">
                <h4>T4 - 2024</h4>
              </div>
              <div className="card-content">
                <div className="info-grid">
                  <div className="info-item">
                    <label>Employer:</label>
                    <span>{data.facts.income.t4_2024.employer}</span>
                  </div>
                  <div className="info-item">
                    <label>Business Number:</label>
                    <span className="mono">{data.facts.income.t4_2024.employer_bn}</span>
                  </div>
                  <div className="info-item">
                    <label>Employment Income:</label>
                    <span className="amount">{formatCurrency(data.facts.income.t4_2024.employment_income)}</span>
                  </div>
                  <div className="info-item">
                    <label>Income Tax Deducted:</label>
                    <span className="amount">{formatCurrency(data.facts.income.t4_2024.income_tax_deducted)}</span>
                  </div>
                  <div className="info-item">
                    <label>CPP Contributions:</label>
                    <span className="amount">{formatCurrency(data.facts.income.t4_2024.cpp)}</span>
                  </div>
                  <div className="info-item">
                    <label>EI Premiums:</label>
                    <span className="amount">{formatCurrency(data.facts.income.t4_2024.ei)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Tax Return Information */}
            {data.facts.income.tax_return_2024 && Object.values(data.facts.income.tax_return_2024).some(v => v !== null) && (
            <div className="card">
              <div className="card-header">
                <h4>Tax Return - 2024</h4>
              </div>
              <div className="card-content">
                <div className="info-grid">
                  {data.facts.income.tax_return_2024.employment_income !== null && (
                  <div className="info-item">
                    <label>Employment Income:</label>
                    <span className="amount">{formatCurrency(data.facts.income.tax_return_2024.employment_income)}</span>
                  </div>
                  )}
                  {data.facts.income.tax_return_2024.total_income !== null && (
                  <div className="info-item">
                    <label>Total Income:</label>
                    <span className="amount">{formatCurrency(data.facts.income.tax_return_2024.total_income)}</span>
                  </div>
                  )}
                  {data.facts.income.tax_return_2024.net_income !== null && (
                  <div className="info-item">
                    <label>Net Income:</label>
                    <span className="amount">{formatCurrency(data.facts.income.tax_return_2024.net_income)}</span>
                  </div>
                  )}
                  {data.facts.income.tax_return_2024.taxable_income !== null && (
                  <div className="info-item">
                    <label>Taxable Income:</label>
                    <span className="amount">{formatCurrency(data.facts.income.tax_return_2024.taxable_income)}</span>
                  </div>
                  )}
                  {data.facts.income.tax_return_2024.federal_tax !== null && (
                  <div className="info-item">
                    <label>Federal Tax:</label>
                    <span className="amount">{formatCurrency(data.facts.income.tax_return_2024.federal_tax)}</span>
                  </div>
                  )}
                  {data.facts.income.tax_return_2024.provincial_tax !== null && (
                  <div className="info-item">
                    <label>Provincial Tax:</label>
                    <span className="amount">{formatCurrency(data.facts.income.tax_return_2024.provincial_tax)}</span>
                  </div>
                  )}
                  {data.facts.income.tax_return_2024.refund_or_balance !== null && (
                  <div className="info-item">
                    <label>Refund/Balance:</label>
                    <span className="amount">{formatCurrency(data.facts.income.tax_return_2024.refund_or_balance)}</span>
                  </div>
                  )}
                </div>
              </div>
            </div>
            )}

            {/* Latest Paystub */}
            <div className="card">
              <div className="card-header">
                <h4>Latest Paystub</h4>
              </div>
              <div className="card-content">
                <div className="info-grid">
                  <div className="info-item">
                    <label>Employer:</label>
                    <span>{data.facts.income.latest_paystub.employer}</span>
                  </div>
                  <div className="info-item">
                    <label>SIN:</label>
                    <span className="mono">{data.facts.income.latest_paystub.sin}</span>
                  </div>
                  <div className="info-item">
                    <label>Pay Period:</label>
                    <span>{formatDate(data.facts.income.latest_paystub.period_start)} - {formatDate(data.facts.income.latest_paystub.period_end)}</span>
                  </div>
                  <div className="info-item">
                    <label>Pay Date:</label>
                    <span>{formatDate(data.facts.income.latest_paystub.pay_date)}</span>
                  </div>
                  <div className="info-item">
                    <label>Gross Pay (Current):</label>
                    <span className="amount">{formatCurrency(data.facts.income.latest_paystub.gross_current)}</span>
                  </div>
                  <div className="info-item">
                    <label>Deductions (Current):</label>
                    <span className="amount">{formatCurrency(data.facts.income.latest_paystub.deductions_current)}</span>
                  </div>
                  <div className="info-item">
                    <label>Net Pay (Current):</label>
                    <span className="amount">{formatCurrency(data.facts.income.latest_paystub.net_current)}</span>
                  </div>
                  <div className="info-item">
                    <label>YTD Gross:</label>
                    <span className="amount">{formatCurrency(data.facts.income.latest_paystub.ytd_gross)}</span>
                  </div>
                  <div className="info-item">
                    <label>YTD Net:</label>
                    <span className="amount">{formatCurrency(data.facts.income.latest_paystub.ytd_net)}</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Assets Information */}
          {data.facts.assets && data.facts.assets.bank_accounts && data.facts.assets.bank_accounts.length > 0 && (
          <section className="assets-info">
            <h3>🏦 Assets & Banking</h3>
            <div className="card">
              <div className="card-header">
                <h4>Bank Accounts</h4>
              </div>
              <div className="card-content">
                {data.facts.assets.bank_accounts.map((account, index) => (
                  <div key={index} className="bank-account">
                    <div className="account-header">
                      <h5>{account.type} Account</h5>
                      <span className="account-number">{account.masked_number}</span>
                    </div>
                    <div className="info-grid">
                      <div className="info-item">
                        <label>Available Balance:</label>
                        <span className="amount">{formatCurrency(account.available_balance)}</span>
                      </div>
                      <div className="info-item">
                        <label>As of Date:</label>
                        <span>{formatDate(account.as_of_date)}</span>
                      </div>
                      <div className="info-item">
                        <label>Source:</label>
                        <span>{account.source}</span>
                      </div>
                    </div>
                  </div>
                ))}
                
                <div className="assets-summary">
                  <div className="info-grid">
                    {data.facts.assets.total_verified_funds !== null && (
                    <div className="info-item total">
                      <label>Total Verified Funds:</label>
                      <span className="amount total-amount">{formatCurrency(data.facts.assets.total_verified_funds)}</span>
                    </div>
                    )}
                    {data.facts.assets.pof_valid_until && (
                    <div className="info-item">
                      <label>PoF Valid Until:</label>
                      <span>{formatDate(data.facts.assets.pof_valid_until)}</span>
                    </div>
                    )}
                    {data.facts.assets.pof_reference && (
                    <div className="info-item">
                      <label>PoF Reference:</label>
                      <span className="mono">{data.facts.assets.pof_reference}</span>
                    </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>
          )}

        </div>
        </div>

        {error && (
          <div className="error-banner">
            {error}
          </div>
        )}

        <div className="analysis-footer">
          <div className="footer-actions">
            <button 
              className="btn btn-success" 
              onClick={() => handleStatusUpdate('Approved')}
              disabled={updating}
            >
              {updating ? '⏳ Updating...' : '✅ Approve'}
            </button>
            <button 
              className="btn btn-danger" 
              onClick={() => handleStatusUpdate('Rejected')}
              disabled={updating}
            >
              {updating ? '⏳ Updating...' : '❌ Reject'}
            </button>
          </div>
          <button 
            className="btn btn-secondary" 
            onClick={handleClose}
            disabled={updating}
          >
            {updating ? 'Saving...' : 'Close Report'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnalysisReportForm;